import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api-client.js";
import type { ExecutionStatus, StepStatus } from "../lib/types.js";

const SSE_BASE = "/api/v1";
const RECONNECT_DELAY_MS = 2000;
const MAX_RETRIES = 3;

export interface UseExecutionMonitorResult {
	isMonitoring: boolean;
	executionId: string | null;
	executionStatus: ExecutionStatus;
	steps: Map<string, StepStatus>;
	elapsed: number;
	start: (executionId: string) => void;
	stop: () => void;
	cancel: () => Promise<void>;
	approve: (approvalKey: string, approved: boolean, reason?: string) => Promise<void>;
}

export function useExecutionMonitor(): UseExecutionMonitorResult {
	const [isMonitoring, setIsMonitoring] = useState(false);
	const [executionId, setExecutionId] = useState<string | null>(null);
	const [executionStatus, setExecutionStatus] = useState<ExecutionStatus>("pending");
	const [steps, setSteps] = useState<Map<string, StepStatus>>(new Map());
	const [elapsed, setElapsed] = useState(0);

	const esRef = useRef<EventSource | null>(null);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const retriesRef = useRef(0);
	const executionIdRef = useRef<string | null>(null);
	const isMonitoringRef = useRef(false);

	const clearTimers = useCallback(() => {
		if (intervalRef.current !== null) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	const closeEventSource = useCallback(() => {
		if (esRef.current) {
			esRef.current.close();
			esRef.current = null;
		}
	}, []);

	const connectSSE = useCallback(
		(execId: string) => {
			closeEventSource();

			const es = new EventSource(`${SSE_BASE}/executions/${execId}/stream`);
			esRef.current = es;

			es.addEventListener("step:update", (event: MessageEvent) => {
				try {
					const stepStatus = JSON.parse(event.data) as StepStatus;
					setSteps((prev) => {
						const next = new Map(prev);
						next.set(stepStatus.stepId, stepStatus);
						return next;
					});
				} catch {
					// ignore malformed events
				}
			});

			es.addEventListener("execution:update", (event: MessageEvent) => {
				try {
					const data = JSON.parse(event.data) as { status: ExecutionStatus };
					setExecutionStatus(data.status);
				} catch {
					// ignore malformed events
				}
			});

			es.addEventListener("execution:complete", (event: MessageEvent) => {
				try {
					const data = JSON.parse(event.data) as { status: ExecutionStatus };
					setExecutionStatus(data.status);
				} catch {
					// ignore malformed events
				}
				es.close();
				esRef.current = null;
				clearTimers();
				setIsMonitoring(false);
				isMonitoringRef.current = false;
			});

			es.onerror = async () => {
				es.close();
				esRef.current = null;

				// Only reconnect if still monitoring and under retry limit
				if (!isMonitoringRef.current || retriesRef.current >= MAX_RETRIES) {
					return;
				}

				retriesRef.current += 1;
				const currentExecId = executionIdRef.current;
				if (!currentExecId) return;

				await new Promise((resolve) => setTimeout(resolve, RECONNECT_DELAY_MS));

				if (!isMonitoringRef.current) return;

				// Re-fetch steps snapshot before reconnecting
				try {
					const stepsData = await api.get<{ steps: StepStatus[] }>(
						`/executions/${currentExecId}/steps`,
					);
					setSteps(new Map(stepsData.steps.map((s) => [s.stepId, s])));
				} catch {
					// proceed with reconnect even if snapshot fails
				}

				if (isMonitoringRef.current) {
					connectSSE(currentExecId);
				}
			};
		},
		[closeEventSource, clearTimers],
	);

	const start = useCallback(
		(execId: string) => {
			// Reset state
			closeEventSource();
			clearTimers();
			retriesRef.current = 0;
			executionIdRef.current = execId;
			isMonitoringRef.current = true;

			setExecutionId(execId);
			setExecutionStatus("pending");
			setSteps(new Map());
			setElapsed(0);
			setIsMonitoring(true);

			// Fetch initial steps snapshot then open SSE
			api
				.get<{ steps: StepStatus[] }>(`/executions/${execId}/steps`)
				.then((data) => {
					setSteps(new Map(data.steps.map((s) => [s.stepId, s])));
				})
				.catch(() => {
					// proceed even if initial fetch fails
				})
				.finally(() => {
					if (isMonitoringRef.current) {
						connectSSE(execId);
					}
				});

			// Start elapsed timer
			const startTime = Date.now();
			intervalRef.current = setInterval(() => {
				setElapsed(Math.floor((Date.now() - startTime) / 1000));
			}, 1000);
		},
		[closeEventSource, clearTimers, connectSSE],
	);

	const stop = useCallback(() => {
		isMonitoringRef.current = false;
		executionIdRef.current = null;
		closeEventSource();
		clearTimers();
		setIsMonitoring(false);
		setExecutionId(null);
		setExecutionStatus("pending");
		setSteps(new Map());
		setElapsed(0);
	}, [closeEventSource, clearTimers]);

	const cancel = useCallback(async () => {
		const execId = executionIdRef.current;
		if (!execId) return;
		await api.post(`/executions/${execId}/cancel`, {});
	}, []);

	const approve = useCallback(async (approvalKey: string, approved: boolean, reason?: string) => {
		const execId = executionIdRef.current;
		if (!execId) return;
		await api.post(`/executions/${execId}/approve`, {
			approval_key: approvalKey,
			approved,
			reason,
		});
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			isMonitoringRef.current = false;
			closeEventSource();
			clearTimers();
		};
	}, [closeEventSource, clearTimers]);

	return {
		isMonitoring,
		executionId,
		executionStatus,
		steps,
		elapsed,
		start,
		stop,
		cancel,
		approve,
	};
}
